<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <div class="headline font-weight-bold mb-4">
          Facturación
        </div>
        <v-breadcrumbs
          divider=">"
          :items="breadcrumbItems"
          class="pa-0"
        ></v-breadcrumbs>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-text>
            <v-row>
              <v-col cols="4">
                <v-text-field
                  v-model="search"
                  label="Buscar"
                  outlined
                  prepend-inner-icon="search"
                  hide-details
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="4">
                <v-autocomplete
                  v-if="currencies.length"
                  v-model="currency"
                  :items="currencies"
                  @change="getData()"
                  item-text="name"
                  item-value="code"
                  label="Moneda"
                  outlined
                  :suffix="currencySelected.symbol"
                  hide-details
                  dense
                  required
                />
              </v-col>
              <v-col cols="4" class="d-flex justify-end">
                <v-btn
                  ref="button-open-export-dialog"
                  @click="toggleDialogExport(true)"
                  color="primary"
                  class="mr-2"
                >
                  <v-icon left>cloud_download</v-icon>
                  {{ $t('general.export') }}
                </v-btn>
              </v-col>
              <v-col cols="12" class="d-flex justify-end">
                <div class="d-flex">
                  <DateBar
                    ref="date-bar"
                    :dateRange="dateRange"
                    @updateRange="
                      (value, range, index) => {
                        dateRange = index
                        endDate = $moment().format('YYYY-MM-DD')
                        changeDates(value, range)
                      }
                    "
                  />
                  <DateFilters
                    ref="date-filters"
                    :initialDate="startDate"
                    :finalDate="endDate"
                    @updateInitialDate="updateDates('startDate', $event)"
                    @updateFinalDate="updateDates('endDate', $event)"
                  />
                </div>
              </v-col>
            </v-row>
          </v-card-text>
          <v-data-table
            :headers="dataTable.headers"
            :items="dataTable.items"
            class="elevation-1"
            pagination.sync="pagination"
            item-key="organization_id"
            :search="search"
            :loading="loading"
          >
            <template v-slot:[`item.name`]="{ item }">
              <v-tooltip right color="black">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-on="on"
                    left
                    :color="getStatusObject(item.status).color"
                  >
                    {{ getStatusObject(item.status).icon }}
                  </v-icon>
                </template>
                <span>{{ getStatusObject(item.status).text }}</span>
              </v-tooltip>
              {{ item.name }}
            </template>
            <template v-slot:[`item.total_donations_organization`]="{ item }">
              {{
                `${item.currency.symbol} ${Number(
                  item.total_donations_organization
                ).toFixed(2)} ${item.currency.code}`
              }}
            </template>
            <template v-slot:[`item.total_donations`]="{ item }">
              {{
                `${currencySelected.symbol} ${Number(
                  item.total_donations
                ).toFixed(2)} ${currencySelected.code}`
              }}
            </template>
            <template v-slot:[`item.total_charges_organization`]="{ item }">
              {{
                `${item.currency.symbol} ${Number(
                  item.total_charges_organization
                ).toFixed(2)} ${item.currency.code}`
              }}
            </template>
            <template v-slot:[`item.total_charges`]="{ item }">
              {{
                `${currencySelected.symbol} ${Number(
                  item.total_charges
                ).toFixed(2)} ${currencySelected.code}`
              }}
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
    <DialogExport
      :module-options="['OrganizationsBillingReport']"
      :start-time="startDate"
      :end-time="endDate"
      :filters="{ currency_code: currency }"
    />
  </v-container>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import { organizationService } from '@/services/organization.service'
import DateFilters from '@/components/Common/DateFilters/Index'
import DateBar from '@/components/Common/DateBar/Index'
import DialogExport from '@/components/Downloads/DialogExport'
import organizationMixin from '@/mixins/organization'

export default {
  name: 'Billing',
  components: {
    DateFilters,
    DateBar,
    DialogExport
  },
  mixins: [organizationMixin],
  data() {
    return {
      loading: false,
      search: '',
      currency: 'USD',
      dateRange: 3,
      startDate: this.$moment()
        .startOf('month')
        .format('YYYY-MM-DD'),
      endDate: this.$moment().format('YYYY-MM-DD'),
      breadcrumbItems: [
        {
          text: 'Addons',
          disabled: false
        },
        {
          text: 'Facturación',
          disabled: true,
          href: ''
        }
      ],
      dataTable: {
        headers: [
          {
            text: 'Organización',
            align: 'left',
            sortable: false,
            value: 'name'
          },
          {
            text: '',
            align: 'right',
            sortable: false,
            value: 'total_donations_organization'
          },
          {
            text: 'Monto de donaciones',
            align: 'right',
            sortable: false,
            value: 'total_donations'
          },
          {
            text: '',
            align: 'right',
            sortable: false,
            value: 'total_charges_organization'
          },
          {
            text: 'Monto de cargos',
            align: 'right',
            sortable: false,
            value: 'total_charges'
          }
        ],
        items: []
      }
    }
  },
  async created() {
    await this.getCurrencies()
    await this.getData()
  },
  computed: {
    ...mapState({
      currencies: ({ countries: { currencies } }) => currencies
    }),
    currencySelected() {
      return this.currencies.find(currency => currency.code === this.currency)
    }
  },
  methods: {
    ...mapActions({
      getCurrencies: 'countries/getCurrencies'
    }),
    ...mapMutations({
      toggleDialogExport: 'downloads/toggleDialogExport'
    }),
    async getData() {
      try {
        this.loading = true
        const { data, total } = (
          await organizationService.getBillingStatistics({
            start_time: this.startDate,
            end_time: this.endDate,
            currency_code: this.currency
          })
        ).data
        this.dataTable.items = data
      } catch (error) {
        console.log(error)
      } finally {
        this.loading = false
      }
    },
    updateDates(property, date) {
      this[property] = date
      const startDate = this.$moment(this.startDate).unix()
      const endDate = this.$moment(this.endDate)
        .endOf('day')
        .unix()
      this.changeDates('customized', startDate, endDate)
    },
    async changeDates(code, start, end) {
      const startDate = !start
        ? this.$moment(this.startDate)
            .startOf('day')
            .unix()
        : start
      const endDate = !end
        ? this.$moment(this.endDate)
            .endOf('day')
            .unix()
        : end

      this.startDate = this.$moment
        .unix(start || startDate)
        .format('YYYY-MM-DD')
      this.endDate = this.$moment.unix(end || endDate).format('YYYY-MM-DD')
      if (code === 'customized') {
        this.dateRange = 7
      }
      await this.getData()
    }
  }
}
</script>
